import { onApplicationError } from 'actions/application.action'
import { API_USER_SIGN_IN } from 'config'
import { clearTenant, getTenantAtLoad, HAS_FRONT_DOOR, reloadForTenant, setTenant } from 'config/tenant.runtime'
import type { Dispatch } from 'react'
import Api from 'services/api'
import { frontDoorLogin, frontDoorSsoExchange } from 'services/apis/frontDoor'
import { exchangeSsoCode } from 'services/apis/sso'
import { clearPendingSso, getPendingSso, safeLandingPath } from 'utils/ssoSession'

import { persistor } from '../reducers'
import {
  AUTH_LOGOUT,
  AUTH_PROFILE_UPDATE,
  AUTH_TOKEN_SET,
  ON_APPLICATION_ERROR,
  SET_HEADER,
  SIGNIN_USER_REQUEST,
  USER_PERMISSIONS_SET,
} from './types'

/**
 * Signs in, resolving which customer the address belongs to when a front door is configured.
 *
 * `askTenant` is invoked instead of completing sign-in when one address authenticates against
 * several customers; the page shows a chooser and calls this again with `tenantId` and
 * `choiceToken`. `landingPage` is where to resume after the reload that a tenant switch needs.
 * Without REACT_APP_GATEWAY_URL this behaves exactly as it always has and talks straight to the
 * backend this build was compiled for.
 */
export function signInRequest(
  params: any,
  ask2fa: Function,
  handleError?: Function,
  askTenant?: (tenants: any[], choiceToken: string) => void,
  landingPage = '/pipeline',
) {
  return async (dispatch: Dispatch<any>) => {
    try {
      dispatch({ type: SIGNIN_USER_REQUEST })
      const queryParams = { ...params }
      const response = HAS_FRONT_DOOR
        ? await frontDoorLogin(queryParams)
        : await Api.post(API_USER_SIGN_IN, queryParams)

      if ((response as any).chooser) {
        dispatch({ type: ON_APPLICATION_ERROR })
        askTenant?.((response as any).tenants, (response as any).choiceToken)
        return
      }

      const {
        tfa = false,
        token,
        user,
        phone,
        hasPasskey = false,
        requireReset = false,
        requirePwChange = false,
        warningPwChange = false,
        tenant,
      } = response as any

      // Point API traffic at the resolved customer straight away. This has to happen before the
      // two-factor branch below: send2faCode and verify2faCode must reach the backend that
      // issued the challenge, not whichever one this bundle was built against.
      // Against the LOAD-time tenant: the hostname may have supplied one mid-load, which
      // does not re-evaluate the display constants, so a restart is still required.
      const switchedTenant = Boolean(tenant && tenant.apiUrl !== getTenantAtLoad()?.apiUrl)
      if (tenant) setTenant(tenant)

      if (tfa) {
        dispatch({ type: ON_APPLICATION_ERROR })
        ask2fa(false, phone, requireReset, requirePwChange, hasPasskey)
      } else {
        dispatch({ type: AUTH_TOKEN_SET, token, user, requirePwChange, warningPwChange })

        if (switchedTenant) {
          // Company name, title and domain are plain module constants evaluated once when the
          // bundle loaded, so a restart is the only thing that makes them match the customer
          // just signed in to. Flush first: redux-persist writes on a throttle, and reloading
          // before it lands would drop the session we just established.
          await persistor.flush()
          // Land on the page the user was heading for. '/' is not a safe target: for every
          // customer except the main one it redirects straight back to the sign-in page.
          reloadForTenant(landingPage)
          return
        }

        ask2fa(true, user?.phone, user?.requireReset, requirePwChange, false)
      }
    } catch (error) {
      // Api dispatches this itself on failure; the front door talks over plain fetch, so it has
      // to. Without it a rejected sign-in shows the user nothing at all: no toast, and the
      // button spins for ever, because ON_APPLICATION_ERROR is what clears isLoading.
      if (HAS_FRONT_DOOR) dispatch(onApplicationError(error))
      handleError && handleError()
    }
  }
}

/**
 * Completes an SSO sign-in by trading the one-time code from the callback for a session.
 *
 * The token deliberately never travels in the URL - the backend redirects here with a short-lived,
 * single-use code, and this is the POST that exchanges it. Dispatches the same AUTH_TOKEN_SET as a
 * password sign-in, so everything downstream of the session is identical.
 */
export function ssoExchangeRequest(code: string, onDone: (redirectTo: string) => void, onError: (message: string) => void) {
  return async (dispatch: Dispatch<any>) => {
    try {
      dispatch({ type: SIGNIN_USER_REQUEST })

      // A sign-in started at the shared front door left itself a note before the redirect, because
      // the browser still does not know which customer it belongs to. With one, the gateway does
      // the exchange and tells us; without one we already know the tenant and can ask it directly.
      const pending = getPendingSso()
      const response: any =
        pending && HAS_FRONT_DOOR ? await frontDoorSsoExchange({ ...pending, code }) : await exchangeSsoCode(code)
      clearPendingSso()

      const { token, user, requirePwChange = false, warningPwChange = false, redirectTo = '', tenant } = response || {}
      if (!token) throw new Error('Single sign-on did not return a session.')

      // Point API traffic at the resolved customer before anything else reads it.
      const switchedTenant = Boolean(tenant && tenant.apiUrl !== getTenantAtLoad()?.apiUrl)
      if (tenant) setTenant(tenant)

      dispatch({ type: AUTH_TOKEN_SET, token, user, requirePwChange, warningPwChange })

      if (switchedTenant) {
        // Same reasoning as signInRequest: company name, title and domain are module constants
        // evaluated once at load, so only a restart makes them match the customer just signed in
        // to. Flush first, because redux-persist writes on a throttle and reloading before it
        // lands would drop the session we just established.
        await persistor.flush()
        reloadForTenant(safeLandingPath(redirectTo))
        return
      }

      onDone(redirectTo)
    } catch (error: any) {
      clearPendingSso()
      dispatch({ type: ON_APPLICATION_ERROR })
      onError(error?.message || 'Single sign-on failed.')
    }
  }
}

export function setUserPermissions(role: any) {
  return { type: USER_PERMISSIONS_SET, role }
}

export function logout() {
  // The next person at this browser should reach the neutral front door, not the previous
  // customer's branding and API.
  if (HAS_FRONT_DOOR) clearTenant()
  return { type: AUTH_LOGOUT }
}

export function authUpdateProfile(profile: any) {
  return { type: AUTH_PROFILE_UPDATE, profile }
}

export function setHeader(data: any) {
  return { type: SET_HEADER, data }
}
